<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { ComputedRef } from 'vue'
import FornecedorDetalhesDialog from '../pesquisa-avancada/components/FornecedorDetalhesDialog.vue'
import { useFornecedorStore } from './store/useFornecedorStore'
import type { IFornecedor } from './types'
import type { IHeader, ITableAction } from '@/pages/types/layoutTable.types'
import type { ITerm } from '@/components/CDF/SearchBar.vue'
import LayoutTable from '@/components/CDF/LayoutTable.vue'

withDefaults(defineProps<{
  isHeader: boolean
}>(), {
  isHeader: true,
})

definePage({
  meta: {
    action: 'list',
    subject: 'fornecedor',
  },
})

const store = useFornecedorStore()

const {
  items,
  itemsPerPage,
  page,
  loading,
  searchTerm,
  isSearching,
  dialogSupplierDetail,
  selectedSupplier,
} = storeToRefs(store)

const {
  loadMore,
  search: onSearch,
  searchAgain: onSearchAgain,
  resetSearch,
  destroy,
  onOrderBy,
  dialogDestroy,
  fetchFornecedorDetail,
} = store

const headers: ComputedRef<IHeader[]> = computed(() => {
  return [
    {
      title: 'Razão Social',
      key: 'razao_social',
    },
    {
      title: 'Nome Fantasia',
      key: 'nome_fantasia',
    },
    {
      title: 'CNPJ',
      key: 'cnpj',
    },
    {
      title: 'WhatsApp',
      key: 'whatsapp',
    },
    {
      title: 'Cidade',
      key: 'cidade',
    },
    {
      title: 'Estado',
      key: 'estado',
    },
  ]
})

const terms: ComputedRef<ITerm[]> = computed(() => {
  return [
    {
      title: 'Razão Social',
      value: 'razao_social',
    },
    {
      title: 'Nome Fantasia',
      value: 'nome_fantasia',
    },
    {
      title: 'CNPJ',
      value: 'cnpj',
    },
    {
      title: 'WhatsApp',
      value: 'whatsapp',
    },
    {
      title: 'Cidade',
      value: 'cidade',
    },
    {
      title: 'Estado',
      value: 'estado',
    },
  ]
})

const actions: ITableAction[] = [
  {
    icon: 'tabler-eye',
    color: 'info',
    can: {
      action: 'read',
      subject: 'fornecedor',
    },
    onClick: (item: IFornecedor) => fetchFornecedorDetail(item.id),
  },
  {
    icon: 'tabler-edit',
    color: 'primary',
    can: {
      action: 'edit',
      subject: 'fornecedor',
    },
    to: (item: IFornecedor) => `/admin/fornecedor/editar/${item.id}`,
  },
  {
    icon: 'tabler-trash',
    color: 'error',
    can: {
      action: 'delete',
      subject: 'fornecedor',
    },
    onClick: (item: IFornecedor) => dialogDestroy(item.id),
  },
]

onBeforeRouteLeave(() => {
  store.$reset()
})
</script>

<template>
  <LayoutTable
    v-model:items="items"
    v-model:loading="loading.items"
    v-model:loading-destroy="loading.destroy"
    v-model:search="searchTerm"
    v-model:is-searching="isSearching"
    :destroy="destroy"
    :is-header="isHeader"
    title="Fornecedores"

    new-item="/admin/fornecedor/cadastrar"
    :headers="headers"
    :actions="actions"
    :items-per-page="itemsPerPage"
    :page="page"

    :load-more="loadMore"
    :terms-search="terms"
    :on-search="onSearch"
    :on-search-again="onSearchAgain"
    :on-reset="resetSearch"
    :on-order-by="onOrderBy"
  />

  <!-- Dialog de Detalhes do Fornecedor -->
  <FornecedorDetalhesDialog
    v-model="dialogSupplierDetail"
    :fornecedor="selectedSupplier"
    @fechar="dialogSupplierDetail = false"
  />
</template>
